import { useState, useMemo } from "react";
import { Col, Row, Table } from "react-bootstrap";
import { useFetch } from "hooks/api";
import Loading from "components/core/blocks/Loading";
import { formatCurrency, formatNumber, formatPercent } from "utils/formatNumber";
import { BsPatchQuestionFill } from "react-icons/bs";
import { trellisPalette } from "components/custom/analytics/palettes";
import { keepPreviousData } from "@tanstack/react-query";

const ReferencePrice = ({ product, marketPlace }) => {
  const [discountChoice, setDiscountChoice] = useState(null);
  const { data, isLoading } = useFetch(
    ["reference_price", product?.id],
    `/merchandise/reference_price`,
    {
      asin: product?.asin,
      current_price: product?.product_price,
      market_place: product?.market_place,
    },
    {
      select: (d) => d.data,
      placeholderData: keepPreviousData,
    },
  );

  const discountRow = useMemo(() => {
    if (!discountChoice) {
      return null;
    }

    for (let i = 0; i < data?.table.length; i++) {
      if (data?.table[i].discount === discountChoice) {
        const table = [];
        const keys = Object.keys(data?.table[i]);
        for (let j = 0; j < keys.length; j++) {
          if (keys[j] !== "discount") {
            const d = data?.table[i][keys[j]];
            if (d !== null) {
              table.push(d);
            }
          }
        }

        return table;
      }
    }

    return null;
  }, [discountChoice, data]);

  if (isLoading) {
    return <Loading height={"300px"} fullPage={false} />;
  }

  return (
    <Row className="p-3">
      <Col xs={12}>
        <Row className="fs-5 text-center pb-3">
          <Col xs={12} className="text-start fs-5">
            <div
              style={{
                padding: "2rem",
                border: "1px solid rgb(238, 238, 238)",
                borderRadius: "6px",
                backgroundColor: "rgb(248, 248, 248)",
                marginTop: "10px",
              }}
            >
              <div>
                The following table shows the current the different combinations of discount and days you need to do in
                order to get the refernce price that you want. Click on the
                <span style={{ color: trellisPalette[0], fontWeight: "bold" }} className="px-1">
                  <BsPatchQuestionFill />
                </span>
                icon to get an explanation of the discount and price combination.
              </div>
            </div>
          </Col>
        </Row>
        <Table>
          <thead>
            <tr>
              <th>&nbsp;</th>
              <th colSpan={data?.prices?.length}>Product Price</th>
            </tr>
            <tr>
              <th>Discount</th>
              {data?.prices?.map((price) => (
                <th key={price}>{formatCurrency(price, marketPlace.marketPlace)}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data?.table.map((row) => (
              <tr key={row.discount}>
                <td className="text-center">
                  <strong>{formatPercent(row.discount, 0)}</strong>
                  <span
                    style={{ color: trellisPalette[0], fontWeight: "bold", cursor: "pointer" }}
                    className="px-1"
                    onClick={() => {
                      setDiscountChoice(row.discount);
                    }}
                  >
                    <BsPatchQuestionFill />
                  </span>
                </td>
                {data?.prices.map((price) => (
                  <td key={price} className="text-center">
                    {row[price] ? `${formatNumber(row[price].days)} days` : "-"}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </Table>

        {discountChoice && (
          <Row className="pt-4">
            <Col xs={12} className="text-start">
              {discountChoice && (
                <div>
                  <strong>Discount: {formatPercent(discountChoice, 0)}</strong>
                  <div>
                    {discountRow.map((r, i) => (
                      <div key={i} className="pt-2">
                        {r.days === 0 &&
                          `You can discount the price to ${formatCurrency(r.price, marketPlace.marketPlace)} today to get the ${formatPercent(discountChoice, 0)} discount badge.`}

                        {r.days !== 0 &&
                          `
                          You can increase the price to ${formatCurrency(r.up_to, marketPlace.marketPlace)} for ${r.days} days then set the price to ${formatCurrency(r.price, marketPlace.marketPlace)} to get the ${formatPercent(discountChoice, 0)} discount badge.`}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </Col>
          </Row>
        )}
      </Col>
    </Row>
  );
};

export default ReferencePrice;
